import React from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import PublishStateIndicator from './PublishStateIndicator';

/**
 * OverflowMenu — Universal item-action container
 * Spec: design-system.md Section 7.13 + Section 8 (Overflow Menu)
 *
 * Built on top of Shadcn dropdown-menu.jsx (Radix UI).
 * Renders a three-dot trigger in the top-right corner of its parent
 * (parent must be position: relative).
 *
 * Props:
 *   items: Array<{ icon: ReactNode, label: string, onClick: () => void, destructive?: boolean }>
 *   publishToggle?: { isPublished: boolean, onToggle: () => void }
 *     If present, renders the Publish toggle as the first row and uses
 *     PublishStateIndicator as the trigger. If absent, uses a neutral three-dot trigger.
 *   placement?: 'item' | 'tab'
 *     'item' (default) — renders on an item card (current behavior).
 *     'tab' — renders on a tab content area (Section 7.14 tab pane overflow menu).
 *     Both share identical trigger and dropdown styling. The distinction
 *     is semantic: tab placement never accepts publishToggle.
 */
export default function OverflowMenu({ items = [], publishToggle, placement = 'item', triggerStyle }) {
  const destructiveItems = items.filter(i => i.destructive);
  const normalItems = items.filter(i => !i.destructive);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {publishToggle ? (
          <PublishStateIndicator isPublished={publishToggle.isPublished} style={triggerStyle} />
        ) : (
          <button
            type="button"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              padding: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              width: 24,
              height: 24,
              ...triggerStyle,
              justifyContent: 'center',
              zIndex: 2,
            }}
            aria-label="Actions"
          >
            {[0, 1, 2].map(i => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.65)',
                  filter: 'drop-shadow(0 0 2.5px rgba(0,0,0,1))',
                }}
              />
            ))}
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="!z-[200]"
        style={{
          background: 'rgba(10, 10, 15, 0.80)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          minWidth: 180,
          padding: '6px 0',
          boxShadow: 'rgba(0,0,0,0.40) 0px 8px 24px',
          animation: 'none',
        }}
      >
        {/* Publish toggle row — first row in publishable apps */}
        {publishToggle && (
          <>
            <button
              type="button"
              onClick={publishToggle.onToggle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                height: 48,
                padding: '0 16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: '#f0f0f5',
                textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: publishToggle.isPublished ? '#10B981' : 'rgba(255,255,255,0.35)',
                  transition: 'background-color 150ms ease',
                  flexShrink: 0,
                }}
              />
              {publishToggle.isPublished ? 'Published' : 'Private'}
            </button>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.04)', margin: 0 }} />
          </>
        )}

        {/* Normal action rows */}
        {normalItems.map((item, idx) => (
          <DropdownMenuItem
            key={idx}
            onClick={item.onClick}
            className=""
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              height: 40,
              padding: '0 16px',
              fontFamily: "'Inter', sans-serif",
              fontSize: 14,
              fontWeight: 400,
              color: '#b0b4c0',
              cursor: 'pointer',
              borderRadius: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              e.currentTarget.style.color = '#f0f0f5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#b0b4c0';
            }}
          >
            {item.icon && (
              <span style={{ display: 'flex', color: '#6b7084', flexShrink: 0 }}>
                {item.icon}
              </span>
            )}
            {item.label}
          </DropdownMenuItem>
        ))}

        {/* Destructive rows — always last, separated by whisper border */}
        {destructiveItems.length > 0 && (
          <>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.04)', margin: 0 }} />
            {destructiveItems.map((item, idx) => (
              <DropdownMenuItem
                key={`destructive-${idx}`}
                onClick={item.onClick}
                className=""
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  height: 40,
                  padding: '0 16px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  color: '#E24B4A',
                  cursor: 'pointer',
                  borderRadius: 0,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(226,75,74,0.06)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {item.icon && (
                  <span style={{ display: 'flex', color: '#E24B4A', flexShrink: 0 }}>
                    {item.icon}
                  </span>
                )}
                {item.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
